import React from 'react';
import styled, { withTheme } from 'styled-components';
import mermaid from 'mermaid';
import Flex from './Flex';
import Text from './Text';
import Spinner from './Spinner';
import Callout from './Callout';
import Dialog from './Dialog';
import Button from './Button';
import Tooltip from './Tooltip';
import Popover from './Popover';
import Menu from './Menu';
import MenuItem from './MenuItem';

const MermaidContainer = styled.div`
  position: relative;

  .mermaid {
    display: flex;
    justify-content: center;

    svg {
      max-width: 100%;
      height: auto;
    }
  }
`;

const ZoomButton = styled(Button)`
  margin-right: 8px;
  z-index: 1;
`;

const DialogContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;

  .mermaid {
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100%;

    svg {
      max-width: 100%;
      max-height: 100%;
      height: auto;
      width: auto;
    }
  }
`;

/**
 * MermaidDiagram - Renders mermaid diagram syntax as SVG
 *
 * @param {string} chart - The mermaid diagram definition
 * @param {object} theme - Theme object from styled-components (injected by withTheme)
 *
 * Example usage:
 * <MermaidDiagram chart="graph TD; A-->B; A-->C;" />
 */
class MermaidDiagram extends React.Component {
  state = {
    svg: null,
    error: null,
    isZoomDialogOpen: false
  };

  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
  }

  async componentDidMount() {
    this.initializeMermaid();
    await this.renderDiagram();
  }

  async componentDidUpdate(prevProps) {
    const { chart, theme } = this.props;

    // Reinitialize if theme changes
    if (prevProps.theme !== theme) {
      this.initializeMermaid();
    }

    if (prevProps.chart !== chart || prevProps.theme !== theme) {
      await this.renderDiagram();
    }
  }

  initializeMermaid() {
    const { theme } = this.props;

    // Initialize mermaid with configuration
    // Available options documented at: https://mermaid.js.org
    mermaid.initialize({
      // startOnLoad: false - Don't auto-render on page load (we control rendering)
      startOnLoad: false,

      // theme: Controls the visual theme of diagrams
      // Options: 'default', 'forest', 'dark', 'neutral', 'base'
      // Match the application's theme
      theme: theme.name === 'dark' ? 'dark' : 'default',

      // fontFamily: Set diagram font to match application font
      fontFamily: theme.fonts.body,

      // securityLevel: Controls what content is allowed in diagrams
      // 'strict' - Maximum security, no scripts/styles
      // 'loose' - Allows more flexibility for custom styling
      // 'antiscript' - Middle ground, prevents scripts but allows styles
      securityLevel: 'loose'

      // Other useful options you can add:
      // themeVariables: {} - Customize colors/fonts for the theme
      // logLevel: 'debug' | 'info' | 'warn' | 'error' | 'fatal' - Control console logging
      // flowchart: { curve: 'basis' } - Flowchart-specific settings
      // sequence: { actorMargin: 50 } - Sequence diagram settings
      // gantt: { titleTopMargin: 25 } - Gantt chart settings
      // journey: { taskMargin: 50 } - User journey settings
      // class: { arrowMarkerAbsolute: false } - Class diagram settings
      // state: { dividerMargin: 10 } - State diagram settings
      // er: { layoutDirection: 'TB' } - Entity relationship diagram settings
      // pie: { textPosition: 0.5 } - Pie chart settings
    });
  }

  async renderDiagram() {
    try {
      const { chart } = this.props;
      const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
      const { svg } = await mermaid.render(id, chart);
      this.setState({ svg, error: null });
    } catch (error) {
      console.error('Mermaid rendering error:', error);
      this.setState({ error: error.message, svg: null });
    }
  }

  handleZoomClick = () => {
    this.setState({ isZoomDialogOpen: true });
  };

  handleCloseDialog = () => {
    this.setState({ isZoomDialogOpen: false });
  };

  handleSaveAsSVG = () => {
    const { svg } = this.state;
    if (!svg) {
      return;
    }

    // Create a blob from the SVG content
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `mermaid-diagram-${Date.now()}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  handleSaveAsMermaid = () => {
    const { chart } = this.props;
    if (!chart) {
      return;
    }

    // Create a blob from the Mermaid text
    const blob = new Blob([chart], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `mermaid-diagram-${Date.now()}.mmd`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  renderExportMenu = () => (
    <Menu>
      <MenuItem text="Save as SVG" label=".svg" onClick={this.handleSaveAsSVG} />
      <MenuItem text="Save as Mermaid" label=".mmd" onClick={this.handleSaveAsMermaid} />
    </Menu>
  );

  render() {
    const { svg, error, isZoomDialogOpen } = this.state;

    if (error) {
      return (
        <Callout intent="danger" title="Error rendering diagram">
          {error}
        </Callout>
      );
    }

    if (!svg) {
      return (
        <Flex alignItems="center" gap="4px" my={1}>
          <Spinner size={12} />
          <Text muted small>
            Loading diagram...
          </Text>
        </Flex>
      );
    }

    /* eslint-disable react/no-danger */
    return (
      <>
        <MermaidContainer>
          <Flex justifyContent="flex-end" mb={1}>
            <Tooltip content="Zoom" position="top">
              <ZoomButton icon="zoom-in" small onClick={this.handleZoomClick} title="Zoom" />
            </Tooltip>
            <Popover position="bottom-right">
              <Tooltip content="Export" position="top">
                <Button icon="export" small title="Export" />
              </Tooltip>
              {this.renderExportMenu()}
            </Popover>
          </Flex>
          <div className="mermaid" ref={this.containerRef} dangerouslySetInnerHTML={{ __html: svg }} />
        </MermaidContainer>

        <Dialog
          isOpen={isZoomDialogOpen}
          onClose={this.handleCloseDialog}
          title="Mermaid Diagram"
          style={{ width: '90vw', height: '90vh' }}
        >
          <DialogContent>
            <div className="mermaid" dangerouslySetInnerHTML={{ __html: svg }} />
          </DialogContent>
          <Dialog.Footer>
            <Flex justifyContent="flex-end" width="100%" gap={1}>
              <Popover position="top">
                <Button icon="export" text="Save" />
                {this.renderExportMenu()}
              </Popover>
              <Button onClick={this.handleCloseDialog} text="Close" />
            </Flex>
          </Dialog.Footer>
        </Dialog>
      </>
    );
    /* eslint-enable react/no-danger */
  }
}

export default withTheme(MermaidDiagram);
